import httpClient from '@/shared/lib/httpClient';
import i18n from '@/shared/lib/i18n';
import {
    ExcelUploadResponse,
    SheetListResponse,
    TableSchemaResponse,
    ExcelTablesResponse,
    ExcelUpload,
    FileInfo,
    TableSchemaDetailResponse,
    UpdateColumnDescriptionResponse,
    ProcessingProgress,
    ExcelUploadProgressEvent,
    ExcelUploadStatus
} from '@/shared/types/excel';

// Types
interface ExcelUploadRequest {
    file: File;
    table_name: string;
    sheet_name?: string;
    column_descriptions_sheet_name?: string;
    metadata_sheet_name?: string;
    project_id: string;
    onProgress: (data: ExcelUploadProgressEvent) => void;
}

interface CsvUploadRequest {
    data_file: File;
    metadata_file?: File;
    descriptions_file?: File;
    table_name: string;
    project_id: string;
    dataDelimiter: string;
    metadataDelimiter: string;
    descriptionsDelimiter: string;
    onProgress: (data: ExcelUploadProgressEvent) => void;
}

// Constants
const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const AUX_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB for auxiliary files

/**
 * Faz upload de um arquivo Excel e cria uma tabela no banco de dados
 */
export async function uploadExcelFile(request: ExcelUploadRequest): Promise<void> {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('table_name', request.table_name);
    formData.append('table_name', request.table_name);
    formData.append('project_id', request.project_id);

    if (request.sheet_name) {
        formData.append('sheet_name', request.sheet_name);
    }
    if (request.column_descriptions_sheet_name) {
        formData.append('column_descriptions_sheet_name', request.column_descriptions_sheet_name);
    }
    if (request.metadata_sheet_name) {
        formData.append('metadata_sheet_name', request.metadata_sheet_name);
    }

    await httpClient.uploadFile<void>(
        '/excel/upload',
        formData,
        { isEventStream: true, onProgress: request.onProgress }
    );
}

/**
 * Upload de trio de arquivos CSV (dados, metadata, descrições)
 */
export async function uploadCsvTrio(request: CsvUploadRequest): Promise<void> {
    const isLargeFile = request.data_file.size > LARGE_FILE_THRESHOLD;

    if (isLargeFile) {
        await uploadCsvTrioChunked(request);
    } else {
        await uploadCsvTrioSingle(request);
    }
}

async function uploadCsvTrioSingle(request: CsvUploadRequest): Promise<void> {
    const formData = new FormData();
    formData.append('data_file', request.data_file);
    formData.append('table_name', request.table_name);
    formData.append('table_name', request.table_name);
    formData.append('project_id', request.project_id);
    formData.append('data_delimiter', request.dataDelimiter);
    formData.append('metadata_delimiter', request.metadataDelimiter);
    formData.append('descriptions_delimiter', request.descriptionsDelimiter);

    if (request.metadata_file) {
        formData.append('metadata_file', request.metadata_file);
    }
    if (request.descriptions_file) {
        formData.append('descriptions_file', request.descriptions_file);
    }

    await httpClient.uploadFile<void>(
        '/csv/upload-trio',
        formData,
        { isEventStream: true, onProgress: request.onProgress }
    );
}

async function uploadCsvTrioChunked(request: CsvUploadRequest): Promise<void> {
    const totalSize = request.data_file.size;
    const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

    let metadataUploadId: string | null = null;
    let descriptionsUploadId: string | null = null;

    if (request.metadata_file) {
        request.onProgress?.({
            event: 'upload_progress',
            progress: 5,
            message: 'Enviando arquivo de metadados...'
        });
        metadataUploadId = await uploadFileChunked(request.metadata_file, 'metadata');
    }

    if (request.descriptions_file) {
        request.onProgress?.({
            event: 'upload_progress',
            progress: 10,
            message: 'Enviando arquivo de descrições...'
        });
        descriptionsUploadId = await uploadFileChunked(request.descriptions_file, 'descriptions');
    }

    const uploadId = await generateUploadId(request.data_file, request.table_name);

    request.onProgress?.({
        event: 'upload_progress',
        progress: 15,
        message: `Iniciando upload em chunks (ID: ${uploadId.substring(0, 20)}...)`
    });

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunk = request.data_file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('table_name', request.table_name);
        formData.append('table_name', request.table_name);
        formData.append('project_id', request.project_id);
        formData.append('data_delimiter', request.dataDelimiter);
        formData.append('metadata_delimiter', request.metadataDelimiter);
        formData.append('descriptions_delimiter', request.descriptionsDelimiter);
        formData.append('original_filename', request.data_file.name || 'data.csv');

        if (metadataUploadId) {
            formData.append('metadata_upload_id', metadataUploadId);
        }
        if (descriptionsUploadId) {
            formData.append('descriptions_upload_id', descriptionsUploadId);
        }

        const uploadProgress = Math.floor(15 + ((chunkIndex + 1) / totalChunks) * 70);
        const remainingSize = (totalChunks - chunkIndex - 1) * CHUNK_SIZE;
        const remainingSizeFormatted = remainingSize > 1024 * 1024 * 1024
            ? `${(remainingSize / (1024 * 1024 * 1024)).toFixed(1)}GB`
            : `${Math.floor(remainingSize / (1024 * 1024))}MB`;

        request.onProgress?.({
            event: 'upload_progress',
            progress: uploadProgress,
            message: chunkIndex === totalChunks - 1
                ? `Enviando último chunk (${chunkIndex + 1} de ${totalChunks})...`
                : `Enviando chunk ${chunkIndex + 1} de ${totalChunks} (resta ~${remainingSizeFormatted})...`
        });

        try {
            const isLastChunk = chunkIndex === totalChunks - 1;

            if (isLastChunk) {
                request.onProgress?.({
                    event: 'upload_progress',
                    progress: uploadProgress,
                    message: `Enviando último chunk e iniciando processamento...`
                });
            }

            await httpClient.uploadFile<void>(
                '/csv/upload-chunk',
                formData,
                {
                    isEventStream: isLastChunk,
                    onProgress: isLastChunk ? request.onProgress : undefined,
                    timeout: isLastChunk ? 300000 : 60000
                }
            );
        } catch (error: any) {
            try {
                await cancelUpload(uploadId);
            } catch (cancelError) {
            }

            request.onProgress?.({
                event: 'error',
                progress: uploadProgress,
                message: error.message
            });

            throw error;
        }
    }
}

async function uploadFileChunked(file: File, type: 'metadata' | 'descriptions'): Promise<string> {
    const totalSize = file.size;
    const totalChunks = Math.ceil(totalSize / AUX_CHUNK_SIZE);
    const uploadId = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * AUX_CHUNK_SIZE;
        const end = Math.min(start + AUX_CHUNK_SIZE, totalSize);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('file_type', type);
        formData.append('original_filename', file.name || `${type}.csv`);

        await httpClient.uploadFile<void>(
            '/csv/upload-auxiliary-chunk',
            formData,
            { timeout: 30000 }
        );
    }

    return uploadId;
}

async function generateUploadId(file: File, tableName: string): Promise<string> {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);

    const fileContent = await file.slice(0, 1024).arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', fileContent);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return `upload_${tableName}_${file.size}_${timestamp}_${hashHex.substring(0, 8)}_${randomSuffix}`;
}

async function checkUploadStatus(uploadId: string): Promise<ExcelUploadStatus | null> {
    try {
        return await httpClient.get<ExcelUploadStatus>(`/csv/upload-status/${uploadId}`);
    } catch (error) {
        return null;
    }
}

export async function cancelUpload(uploadId: string): Promise<void> {
    await httpClient.delete<void>(`/csv/upload/${uploadId}`);
}

/**
 * Obtém as planilhas disponíveis em um arquivo Excel
 */
export async function getExcelSheets(file: File): Promise<SheetListResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.uploadFile<SheetListResponse>('/excel/sheets', formData);
}

/**
 * Lista todas as tabelas Excel disponíveis no banco de dados
 */
export async function listTables(): Promise<ExcelTablesResponse> {
    return httpClient.get<ExcelTablesResponse>('/excel/tables');
}

/**
 * Obtém informações sobre a estrutura de uma tabela específica
 */
export async function getTableSchema(tableName: string): Promise<TableSchemaResponse> {
    return httpClient.get<TableSchemaResponse>(`/excel/tables/${tableName}`);
}

/**
 * Obtém uploads de Excel para uma sessão específica
 */
export async function getProjectExcelUploads(projectId: string): Promise<ExcelUpload[]> {
    return httpClient.get<ExcelUpload[]>(`/projects/${projectId}/excel-uploads`);
}

/**
 * Ativa um upload de Excel específico
 */
export async function activateExcelUpload(projectId: string, uploadId: number): Promise<void> {
    return httpClient.put<void>(`/projects/${projectId}/excel-uploads/${uploadId}/activate`);
}

/**
 * Ativa um upload de Excel criando uma nova tabela
 */
export async function activateExcelUploadWithTable(projectId: string, uploadId: number, tableName: string): Promise<void> {
    return httpClient.put<void>(`/projects/${projectId}/excel-uploads/${uploadId}/activate`, {
        table_name: tableName
    });
}

/**
 * Deleta um upload de Excel específico
 */
export async function deleteExcelUpload(projectId: string, uploadId: number): Promise<void> {
    return httpClient.delete<void>(`/projects/${projectId}/excel-uploads/${uploadId}`);
}

/**
 * Converte ExcelUpload do backend para FileInfo do frontend
 */
export function convertToFileInfo(upload: ExcelUpload): FileInfo {
    return {
        id: upload.id,
        name: upload.original_filename,
        size: "N/A",
        uploadedAt: new Date(upload.created_at),
        isActive: upload.is_active,
        tableName: upload.table_name || undefined,
        sessionId: upload.session_id
    };
}

/**
 * Formata bytes para formato legível
 */
export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Valida se um arquivo é um arquivo Excel válido
 */
export function validateExcelFile(file: File, maxSizeMB: number = 200): { isValid: boolean; error?: string } {
    const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
    ];

    const validExtensions = ['.xls', '.xlsx'];

    const isValidType = validTypes.includes(file.type);
    const isValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType && !isValidExtension) {
        return {
            isValid: false,
            error: i18n.t("excel:invalidFileType")
        };
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
        return {
            isValid: false,
            error: i18n.t("excel:fileTooLarge", { size: maxSizeMB })
        };
    }

    return { isValid: true };
}

/**
 * Valida nome de tabela
 */
export function validateTableName(tableName: string): { isValid: boolean; error?: string } {
    if (!tableName.trim()) {
        return {
            isValid: false,
            error: i18n.t("excel:tableNameRequired")
        };
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(tableName.trim())) {
        return {
            isValid: false,
            error: i18n.t("excel:tableNameInvalid")
        };
    }

    return { isValid: true };
}

/**
 * Obtém schema detalhado de uma tabela com descrições das colunas
 */
export async function getTableSchemaDetail(tableName: string): Promise<TableSchemaDetailResponse> {
    return httpClient.get<TableSchemaDetailResponse>(`/tables/${tableName}/schema`);
}

/**
 * Atualiza a descrição de uma coluna específica
 */
export async function updateColumnDescription(
    tableName: string,
    columnName: string,
    description: string
): Promise<UpdateColumnDescriptionResponse> {
    return httpClient.put<UpdateColumnDescriptionResponse>(
        `/tables/${tableName}/columns/${columnName}/description`,
        { description }
    );
}

/**
 * Atualiza o tipo de uma coluna específica
 */
export async function updateColumnType(
    tableName: string,
    columnName: string,
    newType: string
): Promise<UpdateColumnDescriptionResponse> {
    return httpClient.put<UpdateColumnDescriptionResponse>(
        `/tables/${tableName}/columns/${columnName}/type`,
        { new_type: newType }
    );
}

/**
 * Atualiza a descrição de uma tabela
 */
export async function updateTableDescription(
    tableName: string,
    description: string
): Promise<UpdateColumnDescriptionResponse> {
    return httpClient.put<UpdateColumnDescriptionResponse>(
        `/tables/${tableName}/description`,
        { description }
    );
}

/**
 * Monitora o progresso do processamento do Excel
 */
export async function getProcessingProgress(uploadId: number): Promise<ProcessingProgress> {
    return httpClient.get<ProcessingProgress>(`/excel/progress/${uploadId}`);
}

// Legacy default export for backwards compatibility
const ExcelService = {
    uploadExcelFile,
    uploadCsvTrio,
    cancelUpload,
    getExcelSheets,
    listTables,
    getTableSchema,
    getProjectExcelUploads,
    activateExcelUpload,
    activateExcelUploadWithTable,
    deleteExcelUpload,
    convertToFileInfo,
    formatBytes,
    validateExcelFile,
    validateTableName,
    getTableSchemaDetail,
    updateColumnDescription,
    updateColumnType,
    updateTableDescription,
    getProcessingProgress
};

export default ExcelService;